import React, { useState, useCallback } from "react";
import axios from "axios";
import { SketchPicker } from "react-color";

const csrfToken = () => document.querySelector("[name=csrf-token]")?.content;
const axiosConfig = () => ({
  headers: { "X-CSRF-TOKEN": csrfToken() },
});

const POSITIONS = [
  { key: "lf", label: "LF", row: "forward" },
  { key: "cf", label: "CF", row: "forward" },
  { key: "rf", label: "RF", row: "forward" },
  { key: "ld", label: "LD", row: "defense" },
  { key: "rd", label: "RD", row: "defense" },
];

const PositionSlot = ({ posKey, label, player, isSelected, onSlotClick, onRemove }) => {
  const filled = !!player;
  return (
    <div
      className={`position-slot ${filled ? "position-slot--filled" : "position-slot--empty"} ${isSelected ? "position-slot--selected" : ""}`}
      onClick={onSlotClick}
    >
      {filled ? (
        <>
          <div className="position-slot__image">
            <img
              src={player.image_url || "/assets/black_avatar.png"}
              alt={player.surname}
            />
          </div>
          <div className="position-slot__info">
            <span className="position-slot__name">{player.surname} #{player.number}</span>
            <span className="position-slot__pos">{label}</span>
          </div>
          <button
            className="position-slot__remove"
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            title="Remove"
          >&times;</button>
        </>
      ) : (
        <>
          <div className="position-slot__placeholder">{label}</div>
          <span className="position-slot__hint">Tap to assign</span>
        </>
      )}
    </div>
  );
};

const LineCard = ({ lineData, lineIndex, selectedPlayer, onAssign, onRemove, onColorChange }) => {
  const [showPicker, setShowPicker] = useState(false);
  const forwards = POSITIONS.filter((p) => p.row === "forward");
  const defense = POSITIONS.filter((p) => p.row === "defense");

  return (
    <div className="line-card card mb-3">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Line {lineData.line}</h5>
        <div className="line-card__color-wrapper">
          <div
            className="line-card__color-swatch"
            style={{ backgroundColor: lineData.color }}
            onClick={() => setShowPicker(!showPicker)}
          />
          {showPicker && (
            <div className="line-card__color-popover">
              <div className="line-card__color-cover" onClick={() => setShowPicker(false)} />
              <SketchPicker
                color={lineData.color}
                onChangeComplete={(c) => onColorChange(lineIndex, c.hex)}
              />
            </div>
          )}
        </div>
      </div>
      <div className="card-body line-card__rink">
        <div className="line-card__row">
          <span className="line-card__row-label">Forwards</span>
          <div className="line-card__slots">
            {forwards.map((pos) => (
              <PositionSlot
                key={pos.key}
                posKey={pos.key}
                label={pos.label}
                player={lineData.positions[pos.key]}
                isSelected={false}
                onSlotClick={() => onAssign(lineIndex, pos.key)}
                onRemove={() => onRemove(lineIndex, pos.key)}
              />
            ))}
          </div>
        </div>
        <div className="line-card__row">
          <span className="line-card__row-label">Defense</span>
          <div className="line-card__slots">
            {defense.map((pos) => (
              <PositionSlot
                key={pos.key}
                posKey={pos.key}
                label={pos.label}
                player={lineData.positions[pos.key]}
                isSelected={false}
                onSlotClick={() => onAssign(lineIndex, pos.key)}
                onRemove={() => onRemove(lineIndex, pos.key)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const PlayerPool = ({ players, assignedMap, selectedPlayerId, onSelectPlayer, roleFilter, onRoleFilterChange }) => {
  const filteredPlayers = roleFilter
    ? players.filter((p) => p.role === roleFilter)
    : players;

  return (
    <div className="player-pool">
      <h5 className="player-pool__title">Player Pool</h5>
      <div className="player-pool__filter mb-2">
        <select className="form-control form-control-sm" value={roleFilter} onChange={(e) => onRoleFilterChange(e.target.value)}>
          <option value="">All Positions</option>
          <option value="forward">Forwards</option>
          <option value="defenceman">Defensemen</option>
          <option value="goaltender">Goaltenders</option>
        </select>
      </div>
      <div className="player-pool__list">
        {filteredPlayers.map((player) => {
          const assignment = assignedMap[player.id];
          const isSelected = selectedPlayerId === player.id;
          return (
            <div
              key={player.id}
              className={`pool-player ${isSelected ? "pool-player--selected" : ""} ${assignment ? "pool-player--assigned" : ""}`}
              onClick={() => onSelectPlayer(player.id)}
            >
              <div className="pool-player__image">
                <img
                  src={player.image_url || "/assets/black_avatar.png"}
                  alt={player.surname}
                />
              </div>
              <div className="pool-player__info">
                <span className="pool-player__name">{player.surname} {player.name}</span>
                <span className="pool-player__details">#{player.number} &middot; {player.role}</span>
              </div>
              {assignment && (
                <span className="pool-player__badge">L{assignment.line}</span>
              )}
            </div>
          );
        })}
        {filteredPlayers.length === 0 && (
          <p className="text-muted text-center mt-3">No players found</p>
        )}
      </div>
    </div>
  );
};

const LinesManager = ({ initialLines, availablePlayers, locale }) => {
  const [lines, setLines] = useState(initialLines || []);
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);
  const [roleFilter, setRoleFilter] = useState("");
  const [saving, setSaving] = useState(false);

  // Build a map of playerId -> { line, position }
  const buildAssignedMap = useCallback((currentLines) => {
    const map = {};
    currentLines.forEach((line) => {
      Object.entries(line.positions).forEach(([pos, player]) => {
        if (player) {
          map[player.id] = { line: line.line, position: pos };
        }
      });
    });
    return map;
  }, []);

  const assignedMap = buildAssignedMap(lines);

  const handleAssign = (lineIndex, posKey) => {
    if (!selectedPlayerId) return;

    const player = availablePlayers.find((p) => p.id === selectedPlayerId);
    if (!player) return;

    setLines((prev) => {
      const newLines = prev.map((line) => {
        // Remove this player from any existing position
        const newPositions = { ...line.positions };
        Object.entries(newPositions).forEach(([pos, p]) => {
          if (p && p.id === selectedPlayerId) {
            newPositions[pos] = null;
          }
        });
        return { ...line, positions: newPositions };
      });

      // Assign to the target slot
      newLines[lineIndex] = {
        ...newLines[lineIndex],
        positions: {
          ...newLines[lineIndex].positions,
          [posKey]: player,
        },
      };

      return newLines;
    });

    setSelectedPlayerId(null);
  };

  const handleRemove = (lineIndex, posKey) => {
    setLines((prev) => {
      const newLines = [...prev];
      newLines[lineIndex] = {
        ...newLines[lineIndex],
        positions: {
          ...newLines[lineIndex].positions,
          [posKey]: null,
        },
      };
      return newLines;
    });
  };

  const handleColorChange = (lineIndex, hex) => {
    setLines((prev) => {
      const newLines = [...prev];
      newLines[lineIndex] = { ...newLines[lineIndex], color: hex };
      return newLines;
    });
  };

  const handleSelectPlayer = (playerId) => {
    setSelectedPlayerId((prev) => (prev === playerId ? null : playerId));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = lines.map((line) => ({
        line: line.line,
        color: line.color,
        positions: Object.fromEntries(
          Object.entries(line.positions).map(([pos, player]) => [pos, player ? player.id : ""])
        ),
      }));

      await axios.patch(`/${locale}/admin/lines/save_all`, { lines: payload }, axiosConfig());
      // Toast surfaced by the axios response interceptor.
    } catch (_e) {
      // Toast surfaced by the axios response interceptor.
    }
    setSaving(false);
  };

  return (
    <div className="lines-admin-container">
      <div className="lines-admin-container__cards">
        {lines.map((line, index) => (
          <LineCard
            key={line.line}
            lineData={line}
            lineIndex={index}
            selectedPlayer={selectedPlayerId}
            onAssign={handleAssign}
            onRemove={handleRemove}
            onColorChange={handleColorChange}
          />
        ))}

        <div className="mt-3 mb-4">
          <button className="btn btn-primary btn-lg" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save All Lines"}
          </button>
        </div>
      </div>

      <div className="lines-admin-container__pool">
        <PlayerPool
          players={availablePlayers}
          assignedMap={assignedMap}
          selectedPlayerId={selectedPlayerId}
          onSelectPlayer={handleSelectPlayer}
          roleFilter={roleFilter}
          onRoleFilterChange={setRoleFilter}
        />
      </div>
    </div>
  );
};

export default LinesManager;
